import { __awaiter, __rest } from "tslib";
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { contains, handleCheckoutError, isEmpty, isString, makeCheckError, makeFormErrorsNested, prepareOrder } from '@open-tender/utils';
import { ReducerType } from './types';
import { loginCustomer, selectToken } from './customer';
import { fetchMenu } from './menu';
import { refreshRevenueCenter, setAlert } from './order';
const initialState = {
    check: null,
    form: {
        address: {},
        customer: {},
        details: {},
        discounts: [],
        points: [],
        promoCodes: [],
        surcharges: [],
        tenders: [],
        tip: null,
        token: null
    },
    submitting: false,
    isGuest: false,
    completedOrder: null,
    errors: {},
    loading: 'idle'
};
export var CheckoutActionType;
(function (CheckoutActionType) {
    CheckoutActionType["ValidateOrder"] = "checkout/validateOrder";
    CheckoutActionType["SubmitOrder"] = "checkout/SubmitOrder";
    CheckoutActionType["SubmitOrderPay"] = "checkout/SubmitOrderPay";
    CheckoutActionType["UpdateForm"] = "checkout/updateForm";
})(CheckoutActionType || (CheckoutActionType = {}));
const assembleOrder = (orderData, save = false) => {
    const { order, checkout, groupOrder } = orderData;
    const { orderId, revenueCenter, serviceType, requestedAt, cart, deviceType, prepType, table } = order;
    const { revenue_center_id: revenueCenterId } = revenueCenter || {};
    if (!revenueCenterId || !serviceType || !requestedAt || !cart)
        return null;
    const { form, check } = checkout;
    const { customer, address, details, surcharges, discounts, promoCodes, points, tenders, tip, token, kount_device_session_id } = form;
    const { deposit = null } = check || {};
    // const defaultTip = check ? getDefaultTip(check.config) : null
    const fullAddress = Object.assign(Object.assign({}, order.address), address);
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { customer_id } = customer, customerWithoutId = __rest(customer, ["customer_id"]);
    const data = {
        address: isEmpty(fullAddress) ? null : fullAddress,
        cart: cart,
        cartId: groupOrder.cartId || null,
        customer: customerWithoutId,
        deposit,
        details,
        deviceType,
        discounts,
        orderId,
        points,
        prepType,
        promoCodes,
        requestedAt,
        revenueCenterId,
        serviceType,
        surcharges,
        table,
        tenders,
        tip,
        save,
        token,
        kount_device_session_id
    };
    return prepareOrder(data);
};
const refreshKeys = ['revenue_center_id', 'service_type', 'requested_at'];
const makeRefreshArgs = (order) => ({
    revenueCenterId: order.revenue_center_id,
    serviceType: order.service_type,
    requestedAt: order.requested_at
});
const handleOrderErrors = (err, preparedOrder, dispatch) => {
    const errors = handleCheckoutError(err);
    const keys = Object.keys(errors);
    const args = makeRefreshArgs(preparedOrder);
    if (contains(keys, refreshKeys)) {
        dispatch(refreshRevenueCenter(args));
        return {};
    }
    else if (contains(keys, ['cart'])) {
        const cartError = errors.cart;
        if (isString(cartError)) {
            dispatch(fetchMenu(args));
        }
        else {
            const alert = { type: 'cartCounts', args: { errors: cartError } };
            setTimeout(() => {
                dispatch(setAlert(alert));
            }, 500);
        }
        return {};
    }
    else {
        return errors;
    }
};
export const validateOrder = createAsyncThunk(CheckoutActionType.ValidateOrder, (order, { dispatch, getState, rejectWithValue }) => __awaiter(void 0, void 0, void 0, function* () {
    try {
        const { api } = getState().config;
        const token = selectToken(getState());
        if (!order)
            order = assembleOrder(getState());
        const errors = {};
        if (!order) {
            errors.form = 'Empty cart. Please return to menu.';
            return { check: null, errors };
        }
        const check = yield api.postOrderValidate(order, token);
        const checkError = makeCheckError(check.errors);
        const errMessages = handleCheckoutError(checkError);
        const keys = Object.keys(errMessages);
        const args = makeRefreshArgs(order);
        if (contains(keys, refreshKeys)) {
            dispatch(refreshRevenueCenter(args));
        }
        else if (contains(keys, ['cart'])) {
            const cartError = errMessages.cart;
            if (isString(cartError)) {
                dispatch(fetchMenu(args));
            }
            else {
                const alert = { type: 'cartCounts', args: { errors: cartError } };
                dispatch(setAlert(alert));
            }
        }
        else if (contains(keys, ['promo_codes'])) {
            errors['promo_codes'] = errMessages.promo_codes;
        }
        else if (contains(keys, ['customer'])) {
            errors['customer'] = errMessages.customer;
        }
        return { check, errors };
    }
    catch (err) {
        const errors = makeFormErrorsNested(err);
        return rejectWithValue(errors);
    }
}));
export const submitOrder = createAsyncThunk(CheckoutActionType.SubmitOrder, (args, { dispatch, getState, rejectWithValue }) => __awaiter(void 0, void 0, void 0, function* () {
    const { api } = getState().config;
    if (!api)
        return;
    const token = selectToken(getState());
    const alert = {
        type: 'working',
        args: { text: 'Submitting your order...' }
    };
    dispatch(setAlert(alert));
    const { save = false } = args || {};
    const preparedOrder = assembleOrder(getState(), save);
    if (!preparedOrder) {
        return dispatch(setAlert({ type: 'close' }));
    }
    try {
        const completedOrder = yield api.postOrder(preparedOrder, token);
        const auth = getState().customer.account.auth;
        const { email, password } = preparedOrder.customer || {};
        if (password && !auth) {
            yield dispatch(loginCustomer({ email: email, password }));
        }
        dispatch(setAlert({ type: 'close' }));
        return completedOrder;
    }
    catch (err) {
        dispatch(setAlert({ type: 'close' }));
        const errors = handleOrderErrors(err, preparedOrder, dispatch);
        return rejectWithValue(errors);
    }
}));
export const updateForm = createAsyncThunk(CheckoutActionType.UpdateForm, (payload, { getState }) => __awaiter(void 0, void 0, void 0, function* () {
    const { checkout } = getState();
    const errors = Object.assign({}, checkout.errors);
    const { tenders } = payload;
    if (tenders)
        delete errors.tenders;
    const form = Object.assign(Object.assign({}, checkout.form), payload);
    return {
        form,
        errors
    };
}));
export const submitOrderPay = createAsyncThunk(CheckoutActionType.SubmitOrderPay, (showAlert = false, { dispatch, getState, rejectWithValue }) => __awaiter(void 0, void 0, void 0, function* () {
    const { api } = getState().config;
    if (!api)
        return;
    const token = selectToken(getState());
    dispatch(setSubmitting(true));
    if (showAlert || false) {
        const alert = {
            type: 'working',
            args: { text: 'Submitting your order...' }
        };
        dispatch(setAlert(alert));
    }
    const preparedOrder = assembleOrder(getState());
    if (!preparedOrder) {
        return dispatch(setAlert({ type: 'close' }));
    }
    try {
        const completedOrder = yield api.postOrder(preparedOrder, token);
        const auth = getState().customer.account.auth;
        const { email, password } = preparedOrder.customer || {};
        if (password && !auth) {
            yield dispatch(loginCustomer({ email: email, password }));
        }
        dispatch(setAlert({ type: 'close' }));
        return completedOrder;
    }
    catch (err) {
        dispatch(setAlert({ type: 'close' }));
        const errors = handleOrderErrors(err, preparedOrder, dispatch);
        return rejectWithValue(errors);
    }
}));
const checkoutSlice = createSlice({
    name: ReducerType.Checkout,
    initialState,
    reducers: {
        resetCheckout: () => initialState,
        resetCheck: state => {
            state.check = null;
        },
        resetTip: state => {
            state.form = Object.assign(Object.assign({}, state.form), { tip: null });
        },
        resetErrors: state => {
            state.errors = {};
        },
        resetCompletedOrder: state => {
            state.completedOrder = null;
        },
        setCompletedOrder: (state, action) => {
            state.completedOrder = action.payload;
        },
        setSubmitting: (state, action) => {
            state.submitting = action.payload;
        },
        setGuest: (state, action) => {
            state.isGuest = action.payload;
        },
        setRecaptchaToken: (state, action) => {
            state.form.token = action.payload;
        },
        updateCheckoutCustomer: (state, action) => {
            const account = action.payload;
            const customer = Object.assign({}, state.form.customer);
            const customerId = customer ? customer.customer_id : null;
            let form = Object.assign({}, state.form);
            if (!account) {
                if (customerId) {
                    form = Object.assign(Object.assign({}, form), { customer: {}, promoCodes: [], discounts: [], tenders: [] });
                }
                else {
                    form = Object.assign(Object.assign({}, form), { customer: {}, promoCodes: [] });
                }
            }
            else if (customerId && account.customer_id !== customerId) {
                form = Object.assign(Object.assign({}, form), { customer, discounts: [], tenders: [] });
            }
            else if (account && !customerId) {
                form = Object.assign(Object.assign({}, form), { customer: account, discounts: [], tenders: [] });
            }
            state.form = Object.assign({}, form);
        }
    },
    extraReducers: builder => {
        builder
            .addCase(validateOrder.fulfilled, (state, action) => {
            return Object.assign(Object.assign(Object.assign({}, state), action.payload), { loading: 'idle', error: null });
        })
            .addCase(validateOrder.pending, state => {
            state.loading = 'pending';
        })
            .addCase(validateOrder.rejected, (state, action) => {
            state.errors = action.payload || {};
            state.loading = 'idle';
        })
            .addCase(updateForm.fulfilled, (state, action) => {
            return Object.assign(Object.assign({}, state), action.payload);
        })
            .addCase(submitOrder.fulfilled, (_, action) => {
            return Object.assign(Object.assign({}, initialState), { completedOrder: action.payload });
        })
            .addCase(submitOrder.pending, state => {
            state.loading = 'pending';
        })
            .addCase(submitOrder.rejected, (state, action) => {
            state.loading = 'idle';
            state.submitting = false;
            state.errors = action.payload || {};
        })
            .addCase(submitOrderPay.fulfilled, (_, action) => {
            return Object.assign(Object.assign({}, initialState), { completedOrder: action.payload });
        })
            .addCase(submitOrderPay.pending, state => {
            state.loading = 'pending';
        })
            .addCase(submitOrderPay.rejected, (state, action) => {
            state.loading = 'idle';
            state.submitting = false;
            state.errors = action.payload || {};
        });
    }
});
export const { resetCheckout, resetCheck, resetCompletedOrder, resetErrors, resetTip, setCompletedOrder, setGuest, setSubmitting, updateCheckoutCustomer, setRecaptchaToken } = checkoutSlice.actions;
export const selectCheckout = (state) => state.checkout;
export const selectCheck = (state) => state.checkout.check;
export const selectCompletedOrder = (state) => state.checkout.completedOrder;
export const selectDiscounts = (state) => state.checkout.form.discounts;
export const checkoutReducer = checkoutSlice.reducer;
